import { useCallback } from 'react';
import { useMutation } from '@common-fe/common-fe';

import { api } from '@/api';
import { OrganizationTypes } from '@/common/constants';
import { API_URL } from '@/dtos';
import { AddressesDtoPayload } from '@/modules/employer/types';
import { EmployerIdType } from '@/modules/ExpressSetup/ExpressSetupSignUp/expressSetupSignUp.constants';

export interface Payload {
  id?: number;
  contact_id?: string;
  parent_id?: string;
  external_identifier?: string;
  name?: string;
  short_name?: string;
  type?: OrganizationTypes;
  contactId?: string;
  addresses?: AddressesDtoPayload[];
  organization_status_type?: string;
  normal_properties: {
    tax_id: string;
  };
}

export interface ExpressSetupPayload {
  contactId?: string;
  partner?: string;
  legalName?: string;
  displayName?: string;
  employerIdType?: string;
  employerId?: string;
  taxId: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface Response {
  org_id?: string;
}

const useCreateExpressSetupOrganizationQuery = () => {
  const { mutateAsync, isLoading } = useMutation((newOrganization: Payload) =>
    api.post(API_URL.EXPRESS_SETUPS, newOrganization)
  );

  const handleCreate = useCallback(
    async (newOrganization: ExpressSetupPayload) => {
      const payload: Payload = {
        type: OrganizationTypes.company,
        contact_id: newOrganization?.contactId,
        parent_id: newOrganization?.partner,
        name: newOrganization?.legalName,
        short_name: newOrganization?.displayName,
        ...(newOrganization?.employerIdType === EmployerIdType.CUSTOM
          ? { external_identifier: newOrganization?.employerId }
          : {}),
        normal_properties: {
          tax_id: newOrganization?.taxId,
        },
        addresses: [
          {
            line1: newOrganization?.addressLine1,
            ...(newOrganization?.addressLine2 ? { line2: newOrganization?.addressLine2 } : {}),
            city: newOrganization?.city,
            state: newOrganization?.state,
            zipcode: newOrganization?.zipCode,
            preferred: true,
            country_id: 1,
            address_type: 'PO_BOX',
          },
        ],
      };

      const response = await mutateAsync(payload);
      return response?.data as Response;
    },
    [mutateAsync]
  );

  return {
    handleCreate,
    isLoading,
  };
};

export default useCreateExpressSetupOrganizationQuery;
